import React, { useEffect, useMemo } from 'react';
import type {
  UseFormReturn,
  SubmitHandler,
  UseFormProps,
  Path,
  SubmitErrorHandler,
  DefaultValues,
  FieldValues,
} from 'react-hook-form';
import type { AnyObject, ObjectSchema } from 'yup';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

type ServerErrors<TFormValues extends FieldValues> = Partial<Record<Path<TFormValues>, string>>;

type FormProps<TFormValues extends FieldValues> = {
  onSubmit: SubmitHandler<TFormValues>;
  onError?: SubmitErrorHandler<TFormValues>;
  children: (methods: UseFormReturn<TFormValues>) => React.ReactNode;
  useFormProps?: UseFormProps<TFormValues>;
  validationSchema?: ObjectSchema<TFormValues, AnyObject, any, ''>;
  serverError?: ServerErrors<TFormValues> | null;
  resetValues?: DefaultValues<TFormValues> | null;
  className?: string;
  formProps?: React.FormHTMLAttributes<HTMLFormElement>;
};

/**
 * Generic Form wrapper:
 * - Cho phép useFormProps override hoàn toàn (bao gồm resolver).
 * - Nếu user không truyền resolver và validationSchema tồn tại, dùng yupResolver.
 * - Cast resolver từ yupResolver qua `unknown` -> UseFormProps[...] để tắt lỗi TS về incompatibility.
 */
export const Form = <TFormValues extends Record<string, any> = Record<string, any>>({
  onSubmit,
  onError,
  children,
  useFormProps,
  validationSchema,
  serverError,
  resetValues,
  className,
  formProps,
}: FormProps<TFormValues>) => {
  const formOptions = useMemo<UseFormProps<TFormValues>>(() => {
    const base: UseFormProps<TFormValues> = { ...(useFormProps ?? ({} as UseFormProps<TFormValues>)) };
    if (!base.resolver && validationSchema) {
      base.resolver = yupResolver(validationSchema as any) as any;
    }

    return base;
  }, [useFormProps, validationSchema]);

  const methods = useForm<TFormValues>(formOptions);

  useEffect(() => {
    if (!serverError) return;
    Object.entries(serverError).forEach(([key, value]) => {
      if (value == null || value === '') return;
      methods.setError(key as Path<TFormValues>, {
        type: 'server',
        message: String(value),
      });
    });
  }, [serverError]);

  useEffect(() => {
    if (resetValues != null) {
      methods.reset(resetValues as DefaultValues<TFormValues>);
    }
  }, [resetValues]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit, onError)} noValidate className={className} {...formProps}>
        {children(methods)}
      </form>
    </FormProvider>
  );
};

export default Form;
